import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { RequireAuth } from '../lib/auth';
import { AppShell } from './AppShell';
import { RouteError } from './RouteError';
import { StaffIndexRedirect, StaffShell } from './StaffShell';

type Loader = () => Promise<{ default: ComponentType }>;
/** Lazy route: each page is its own chunk (the map, editor and dispatch console stay out of the home bundle). */
const page = (load: Loader): Pick<RouteObject, 'lazy'> => ({ lazy: async () => ({ Component: (await load()).default }) });

export const routes: RouteObject[] = [
  {
    element: <AppShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, ...page(() => import('../pages/resident/HomePage')) },
      { path: 'calendar', ...page(() => import('../pages/resident/CalendarPage')) },
      { path: 'p/:postId', ...page(() => import('../pages/resident/PostPage')) },
      { path: 'p/:postId/register', ...page(() => import('../pages/resident/RegisterPage')) },
      { path: 'tickets/:registrationId', ...page(() => import('../pages/resident/TicketPage')) },
      { path: 'offices', ...page(() => import('../pages/resident/OfficesPage')) },
      { path: 'offices/:slug', ...page(() => import('../pages/resident/OfficePage')) },
      { path: 'mine', ...page(() => import('../pages/resident/MyActivityPage')) },
      { path: 'following', ...page(() => import('../pages/resident/FollowingPage')) },
      { path: 'notifications', ...page(() => import('../pages/resident/NotificationsPage')) },
      { path: 'settings', ...page(() => import('../pages/resident/SettingsPage')) },
      { path: 'more', ...page(() => import('../pages/resident/MorePage')) },
      { path: 'privacy', ...page(() => import('../pages/resident/PrivacyPage')) },
      { path: 'about', ...page(() => import('../pages/resident/AboutPage')) },
      // Safety & preparedness
      { path: 'map', ...page(() => import('../features/safety/MapPage')) },
      { path: 'hotlines', ...page(() => import('../features/safety/HotlinesPage')) },
      { path: 'emergency', ...page(() => import('../features/emergency/EmergencyPage')) },
      { path: 'emergency/track/:ref', ...page(() => import('../features/emergency/TrackPage')) },
      { path: 'respond/:ref', ...page(() => import('../features/emergency/RespondPage')) },
      { path: 'crop-damage', ...page(() => import('../features/crop/CropReportPage')) },
      { path: 'crop-damage/track/:ref', ...page(() => import('../features/crop/CropTrackPage')) },
      // Services and requests (Phase 2)
      { path: 'services', ...page(() => import('../features/services/ServicesPage')) },
      { path: 'services/:slug', ...page(() => import('../features/services/ServicePage')) },
      { path: 'services/:slug/apply', ...page(() => import('../features/requests/ApplyPage')) },
      { path: 'civil-registry', ...page(() => import('../features/services/CivilRegistryPage')) },
      { path: 'charter', ...page(() => import('../features/services/CharterPage')) },
      { path: 'report', ...page(() => import('../features/requests/ReportConcernPage')) },
      { path: 'requests', ...page(() => import('../features/requests/MyRequestsPage')) },
      { path: 'requests/:ref', ...page(() => import('../features/requests/RequestPage')) },
      { path: 'taxes', ...page(() => import('../features/taxes/TaxesPage')) },
      { path: 'feedback', ...page(() => import('../features/feedback/FeedbackPage')) },
      // Phase 3
      { path: 'search', ...page(() => import('../features/search/SearchPage')) },
      { path: 'guides', ...page(() => import('../features/guides/GuidesPage')) },
      { path: 'guides/:slug', ...page(() => import('../features/guides/GuidePage')) },
      { path: 'market-prices', ...page(() => import('../features/market/MarketPricesPage')) },
      { path: 'recycling', ...page(() => import('../features/recycling/RecyclingPage')) },
      { path: 'recycling/:ref', ...page(() => import('../features/recycling/PickupPage')) },
      { path: 'facilities', ...page(() => import('../features/facilities/FacilitiesPage')) },
      { path: 'facilities/bookings/:ref', ...page(() => import('../features/facilities/BookingPage')) },
      { path: 'facilities/:slug', ...page(() => import('../features/facilities/FacilityPage')) },
      { path: 'proposals', ...page(() => import('../features/community/ProposalsPage')) },
      { path: 'proposals/new', ...page(() => import('../features/community/ProposalFormPage')) },
      { path: 'proposals/:id', ...page(() => import('../features/community/ProposalPage')) },
      { path: 'lost-found', ...page(() => import('../features/community/LostFoundPage')) },
      { path: 'lost-found/new', ...page(() => import('../features/community/LostFoundFormPage')) },
      { path: 'lost-found/:id', ...page(() => import('../features/community/LostFoundItemPage')) },
      { path: 'tourism', ...page(() => import('../features/tourism/TourismPage')) },
      { path: 'tourism/spots/:slug', ...page(() => import('../features/tourism/SpotPage')) },
      { path: 'tourism/trails/:slug', ...page(() => import('../features/tourism/TrailPage')) },
      { path: 'tourism/visits/:code', ...page(() => import('../features/tourism/VisitTicketPage')) },
      { path: 'tourism/guides', ...page(() => import('../features/tourism/GuidesDirectoryPage')) },
      { path: 'transparency', ...page(() => import('../features/transparency/TransparencyPage')) },
      { path: 'documents', ...page(() => import('../features/transparency/DocumentsPage')) },
      // Auth
      { path: 'signin', ...page(() => import('../pages/auth/SignInPage')) },
      { path: 'signup', ...page(() => import('../pages/auth/SignUpPage')) },
      { path: 'forgot-password', ...page(() => import('../pages/auth/ForgotPasswordPage')) },
      { path: 'reset-password', ...page(() => import('../pages/auth/ResetPasswordPage')) },
      { path: 'verify-email', ...page(() => import('../pages/auth/VerifyEmailPage')) },
      { path: 'accept-invite', ...page(() => import('../pages/auth/AcceptInvitePage')) },
      { path: '*', ...page(() => import('../pages/resident/NotFoundPage')) },
    ],
  },
  {
    path: 'staff',
    errorElement: <RouteError />,
    element: <RequireAuth staff><StaffIndexRedirect /></RequireAuth>,
  },
  {
    path: 'staff/:officeId',
    errorElement: <RouteError />,
    element: <RequireAuth staff><StaffShell mode="office" /></RequireAuth>,
    children: [
      { index: true, ...page(() => import('../features/staff/DashboardPage')) },
      { path: 'posts', ...page(() => import('../features/staff/PostsPage')) },
      { path: 'posts/new', ...page(() => import('../features/staff/PostEditorPage')) },
      { path: 'posts/:postId/edit', ...page(() => import('../features/staff/PostEditorPage')) },
      { path: 'posts/:postId/registrations', ...page(() => import('../features/staff/RegistrationsPage')) },
      { path: 'checkin', ...page(() => import('../features/staff/CheckInPage')) },
      { path: 'profile', ...page(() => import('../features/staff/OfficeProfilePage')) },
      { path: 'team', ...page(() => import('../features/staff/TeamPage')) },
      { path: 'dispatch', ...page(() => import('../features/emergency/staff/DispatchPage')) },
      { path: 'hq', ...page(() => import('../features/emergency/staff/HqPage')) },
      { path: 'crop', ...page(() => import('../features/crop/staff/CropDashboardPage')) },
      { path: 'crop/print', ...page(() => import('../features/crop/staff/CropPrintPage')) },
      { path: 'safety', ...page(() => import('../features/safety/staff/SafetyAdminPage')) },
      { path: 'emergency-mode', ...page(() => import('../features/safety/staff/EmergencyModePage')) },
      // Phase 2
      { path: 'requests', ...page(() => import('../features/requests/staff/RequestsPage')) },
      { path: 'requests/:requestId', ...page(() => import('../features/requests/staff/RequestDetailPage')) },
      { path: 'services', ...page(() => import('../features/services/staff/ServicesAdminPage')) },
      { path: 'services/new', ...page(() => import('../features/services/staff/ServiceEditorPage')) },
      { path: 'services/:serviceId/edit', ...page(() => import('../features/services/staff/ServiceEditorPage')) },
      { path: 'revenue', ...page(() => import('../features/taxes/staff/RevenueDeskPage')) },
      { path: 'alerts', ...page(() => import('../features/alerts/staff/AlertsPage')) },
      { path: 'feedback', ...page(() => import('../features/feedback/staff/FeedbackInsightsPage')) },
      // Phase 3
      { path: 'guides', ...page(() => import('../features/guides/staff/OfficeGuidesPage')) },
      { path: 'market-prices', ...page(() => import('../features/market/staff/MarketAdminPage')) },
      { path: 'recycling', ...page(() => import('../features/recycling/staff/RecyclingAdminPage')) },
      { path: 'facilities', ...page(() => import('../features/facilities/staff/FacilitiesAdminPage')) },
      { path: 'moderation', ...page(() => import('../features/community/staff/ModerationPage')) },
      { path: 'tourism', ...page(() => import('../features/tourism/staff/TourismAdminPage')) },
      { path: 'transparency', ...page(() => import('../features/transparency/staff/TransparencyAdminPage')) },
      { path: 'ordinances', ...page(() => import('../features/transparency/staff/OrdinancesAdminPage')) },
    ],
  },
  {
    path: 'admin',
    errorElement: <RouteError />,
    element: <RequireAuth staff admin><StaffShell mode="admin" /></RequireAuth>,
    children: [
      { index: true, ...page(() => import('../features/admin/AdminHomePage')) },
      { path: 'offices', ...page(() => import('../features/admin/OfficesAdminPage')) },
      { path: 'staff', ...page(() => import('../features/admin/StaffAdminPage')) },
      { path: 'settings', ...page(() => import('../features/admin/SettingsAdminPage')) },
      { path: 'categories', ...page(() => import('../features/admin/CategoriesAdminPage')) },
      { path: 'audit', ...page(() => import('../features/admin/AuditPage')) },
      { path: 'sms-log', ...page(() => import('../features/admin/SmsLogPage')) },
      { path: 'emergency-mode', ...page(() => import('../features/safety/staff/EmergencyModePage')) },
      { path: 'hotlines', ...page(() => import('../features/safety/admin/HotlinesAdminPage')) },
      { path: 'responders', ...page(() => import('../features/emergency/admin/RespondersAdminPage')) },
      { path: 'disaster-events', ...page(() => import('../features/crop/admin/DisasterEventsAdminPage')) },
      { path: 'report-categories', ...page(() => import('../features/requests/admin/ReportCategoriesPage')) },
      { path: 'holidays', ...page(() => import('../features/requests/admin/HolidaysPage')) },
      { path: 'guides', ...page(() => import('../features/guides/admin/GuidesAdminPage')) },
    ],
  },
  { path: 'index.html', element: <Navigate to="/" replace /> },
];

export const createRouter = () => createBrowserRouter(routes);
